import { forwardRef, useRef, useEffect, MutableRefObject } from 'react'

import _ from 'lodash'
import { styled, css } from 'styled-components'

import { focusNextFocusableElement } from 'admin/helpers'

const Wrapper = styled.div<{ fullHeight?: boolean; ref: any }>`
  max-height: inherit;
  ${(p) =>
    p.fullHeight &&
    css`
      height: 100%;
    `}
`

type Props = {
  children: any
  arrowFocus?: boolean
  focusOnMount?: boolean
  returnFocusOnUnmount?: boolean
  onArrowUp?: (event: any) => void
  onArrowDown?: (event: any) => void
  onEnter?: (event: any) => void
  stopPropagationOnEnter?: boolean
  preventDefaultOnEnter?: boolean
  onEscape?: (event: any) => void
  onSpace?: (event: any) => void
  onKeyDown?: (event: any) => void
  onTypeCharacter?: (event: any) => void
}

const KeyboardNavArea = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    children,
    arrowFocus,
    focusOnMount,
    returnFocusOnUnmount,
    onArrowUp,
    onArrowDown,
    onEnter,
    stopPropagationOnEnter,
    preventDefaultOnEnter,
    onEscape,
    onSpace,
    onKeyDown,
    onTypeCharacter,
    ...rest
  } = props

  const localRef = useRef<Element | null>(null)

  const $ref = ref ?? localRef

  const previousFocusRef = useRef<Element | null>(null)

  useEffect(() => {
    previousFocusRef.current = document.activeElement

    if (focusOnMount) {
      _.delay(() => {
        focusNextFocusableElement(($ref as MutableRefObject<HTMLInputElement>).current)
      }, 10)
    }
    return () => {
      if (focusOnMount && returnFocusOnUnmount && previousFocusRef.current) {
        ;(previousFocusRef as MutableRefObject<HTMLInputElement>).current.focus()
      }
    }
  }, []) // eslint-disable-line

  const handleKeyDown = (event: any) => {
    switch (event.key) {
      case 'Enter':
        if (onEnter) onEnter(event)
        if (stopPropagationOnEnter) event.stopPropagation()
        if (preventDefaultOnEnter) event.preventDefault()
        break
      case 'Spacebar':
      case ' ':
        if (onSpace) onSpace(event)
        break
      case 'Escape':
        if (onEscape) onEscape(event)
        break
      case 'ArrowUp':
        if (onArrowUp) onArrowUp(event)
        if (arrowFocus) {
          focusNextFocusableElement(($ref as MutableRefObject<HTMLInputElement>).current, { increment: -1 })
        }
        break
      case 'ArrowDown':
        if (onArrowDown) onArrowDown(event)
        if (arrowFocus) {
          focusNextFocusableElement(($ref as MutableRefObject<HTMLInputElement>).current, { increment: 1 })
        }
        break
      default: {
        if (_.size(event.key) === 1 && onTypeCharacter) {
          onTypeCharacter(event.key)
        }
      }
    }
    if (onKeyDown) onKeyDown(event)
  }

  return (
    <Wrapper {...rest} onKeyDown={handleKeyDown} ref={$ref}>
      {children}
    </Wrapper>
  )
})

KeyboardNavArea.displayName = 'KeyboardNavArea'

export default KeyboardNavArea
